// Billing — every Stripe-touching entity persisted to Firestore.
//
// Source-of-truth lives in Stripe. We mirror exactly what the dashboard +
// admin UI need to query, so we never have to hit the Stripe API for
// listings. All amounts in **cents** (€1.00 → 100). Currency = 'eur' by
// default; carried explicitly on every doc to make multi-currency a
// non-breaking change later.
//
// Collections:
//   creditPurchases/{id}            one-time pack purchases (Stripe Checkout)
//   subscriptions/{userId}           current sub state (one per user — keyed by uid)
//   subscriptionEvents/{id}          append-only history of subscription changes
//   refunds/{id}                     one row per Stripe refund
//   stripeEventsProcessed/{eventId}  idempotency guard for Stripe webhooks
//   billingPortalSessions/{id}       audit of portal-session opens (optional, lightweight)

// ---------------------------------------------------------------------------
// Plans + price-mapping (mirrors ls-app's pricing — single source of truth here)
// ---------------------------------------------------------------------------

// Recurring subscription tiers. Keys MUST match the env-var suffix:
//   STRIPE_SUBSCRIPTION_BASIC_PRICE_ID   → 'basic'
//   STRIPE_SUBSCRIPTION_STARTER_PRICE_ID → 'starter'
//   STRIPE_SUBSCRIPTION_GROWTH_PRICE_ID  → 'growth'
export type SubscriptionTier = 'basic' | 'starter' | 'growth';

export type SubscriptionTierConfig = {
  tier: SubscriptionTier;
  name: string; // human-facing
  creditsPerMonth: number; // resets on every renewal
  pricePerMonthEur: number; // EUR (not cents) — display only
  pricePerMonthCents: number; // for sanity-checking webhook amounts
};

export const SUBSCRIPTION_TIERS: Record<SubscriptionTier, SubscriptionTierConfig> = {
  basic: {
    tier: 'basic',
    name: 'Basic',
    creditsPerMonth: 100,
    pricePerMonthEur: 190,
    pricePerMonthCents: 19000,
  },
  starter: {
    tier: 'starter',
    name: 'Starter',
    creditsPerMonth: 250,
    pricePerMonthEur: 290,
    pricePerMonthCents: 29000,
  },
  growth: {
    tier: 'growth',
    name: 'Growth',
    creditsPerMonth: 500,
    pricePerMonthEur: 490,
    pricePerMonthCents: 49000,
  },
};

// LEA-299: explicit tier ordering so callers can deterministically tell an
// upgrade from a downgrade without re-deriving it from credits/price every
// time. basic < starter < growth. Higher rank = bigger plan.
export const SUBSCRIPTION_TIER_RANK: Record<SubscriptionTier, number> = {
  basic: 0,
  starter: 1,
  growth: 2,
};

// Returns <0 if `a` is a lower plan than `b`, 0 if equal, >0 if higher.
export function compareSubscriptionTiers(a: SubscriptionTier, b: SubscriptionTier): number {
  return SUBSCRIPTION_TIER_RANK[a] - SUBSCRIPTION_TIER_RANK[b];
}

// True when moving from `from` → `to` is a downgrade (a strictly smaller plan).
export function isTierDowngrade(from: SubscriptionTier, to: SubscriptionTier): boolean {
  return compareSubscriptionTiers(to, from) < 0;
}

// One-time credit packs. Key = credit count, value = price in EUR cents.
// Mapping is enforced via STRIPE_PRODUCT_ID_<credits> env vars and validated
// at boot — purchasing N credits MUST go through the pre-registered product
// so admin sees consistent line-items in Stripe.
export type CreditPackSize = 1000 | 5000 | 10000 | 50000;

// ---------------------------------------------------------------------------
// Kontakt-Pakete (LEA-516) — die Währung des Reveal-Modells
// ---------------------------------------------------------------------------
//
// BEWUSST getrennt von CREDIT_PACKS oben, obwohl drei Grössen gleich heissen.
// Im alten Modell ist ein Credit ein AUSGELIEFERTER LEAD (100er-Paket = 79 €),
// im neuen ein FREIGESCHALTETER KONTAKT (100er-Paket = 100 €). Dieselbe Zahl,
// anderes Produkt, anderer Preis. Die beiden Leitern zu vermischen wäre genau
// das, was das Ticket ausdrücklich verbietet ("zwei widersprüchliche
// Preisleitern in Stripe sind das, was wir vermeiden wollen") — und im Code
// wäre es noch schlimmer als in Stripe, weil `CREDIT_PACKS[100].priceEur`
// stillschweigend den falschen Preis bewerben würde.
//
// Die Alt-Pakete vom 05.08. werden in Stripe archiviert, nicht wiederverwendet.

export type ContactPackSize = 100 | 250 | 500;

export type ContactPackConfig = {
  contacts: ContactPackSize;
  priceCents: number;
  priceEur: number;
  /**
   * Was der Erstkäufer zahlt — ein FESTER Betrag pro Paket, kein abgeleiteter
   * Prozentsatz. Genau das ist Janiks Korrektur vom 19.08.: die Erstkaufpreise
   * sind Schwellenbeträge (49 / 99 / 175 €), und die fallen bei 99/199/349
   * nicht auf einen gemeinsamen Prozentsatz. Effektiv sind es 50,5 % / 50,25 %
   * / 49,9 % — deshalb darf im UI auch nie „−50 %" stehen, sondern immer
   * Streichpreis → Erstkaufpreis.
   */
  firstPurchaseCents: number;
  /** Nur fürs UI — „beliebt"-Badge auf der mittleren Karte. */
  popular?: boolean;
};

/**
 * Die drei Kontakt-Pakete — Preise nach Janiks Korrektur vom 19.08. (06:22),
 * die seine Rundungsantwort von 06:17 ersetzt.
 *
 * Regulär bleibt es bei der ursprünglichen Ticket-Tabelle 99/199/349. Die
 * Erstkaufpreise sind bewusst SCHWELLENBETRÄGE: 49 statt 50, 99 statt 100,
 * das 500er glatt 175. Genau deshalb ist der Rabatt kein Prozentsatz mehr —
 * bei 99/199/349 fallen diese drei Zahlen auf 50,5 % / 50,25 % / 49,9 %, also
 * nicht auf einen gemeinsamen Wert.
 *
 * Damit ist auch die ONE-coupon-Constraint der Beschreibung aufgehoben (ein
 * Product-Set bleibt): jedes Paket bekommt einen eigenen Festbetrags-Coupon,
 * `amount_off` = priceCents − firstPurchaseCents, in Stripe auf genau seinen
 * eigenen Price beschränkt.
 *
 * Pro Kontakt: 0,99 € · 0,80 € · 0,70 € regulär, 0,49 € · 0,40 € · 0,35 € im
 * Erstkauf. Die Story dazu ist NICHT „1 € pro Kontakt" (das stimmte nur in der
 * verworfenen 100er-Variante), sondern „ab 70 Cent pro Kontakt — beim ersten
 * Kauf ab 35".
 *
 * Zum 500er: 0,35 €/Kontakt liegt laut den gemessenen Kosten ($0,28–0,42 pro
 * erreichbarem Kontakt) schlimmstenfalls grob break-even. Das ist Janiks
 * bewusster Akquisitionspreis, einmal pro Kunde und durch die Packgrösse
 * gedeckelt — wenn die Unit Economics nach der LEA-515-Messung weiter unter
 * Wasser sind, wird das als PREISENTSCHEIDUNG neu aufgemacht und nicht hier
 * stillschweigend korrigiert.
 */
export const CONTACT_PACKS: Record<ContactPackSize, ContactPackConfig> = {
  100: { contacts: 100, priceCents: 9900, priceEur: 99, firstPurchaseCents: 4900 },
  250: { contacts: 250, priceCents: 19900, priceEur: 199, firstPurchaseCents: 9900, popular: true },
  500: { contacts: 500, priceCents: 34900, priceEur: 349, firstPurchaseCents: 17500 },
};

/** Was der Erstkäufer zahlt. Fester Betrag pro Paket, nicht gerechnet. */
export function firstPurchasePriceCents(size: ContactPackSize): number {
  return CONTACT_PACKS[size].firstPurchaseCents;
}

/**
 * Der Nachlass in Cent — das, was als `amount_off` im Stripe-Coupon steht.
 *
 * Abgeleitet und nicht separat gepflegt: Preis und Nachlass zweimal von Hand
 * zu führen ist genau, wie Katalog und Abbuchung auseinanderlaufen. Wer den
 * Coupon in Stripe anlegt, liest den Betrag hier ab.
 */
export function firstPurchaseDiscountCents(size: ContactPackSize): number {
  const pack = CONTACT_PACKS[size];
  return pack.priceCents - pack.firstPurchaseCents;
}

export type CreditPackConfig = {
  credits: CreditPackSize;
  priceCents: number;
  priceEur: number;
};

// LEA-379 (Prism Stufe 2): the tripwire grants 50 credits (= 50 leads) as a
// real pack_purchase on the 49 € payment — so the day-4 first-cycle void
// PRESERVES them (voidNonPackCreditsBeforeFirstCycle keeps net pack credits).
export const PRISM_TRIPWIRE_CREDITS = 50;

export const CREDIT_PACKS: Record<CreditPackSize, CreditPackConfig> = {
  1000: { credits: 1000, priceCents: 79000, priceEur: 790 },
  5000: { credits: 5000, priceCents: 349000, priceEur: 3490 },
  10000: { credits: 10000, priceCents: 549000, priceEur: 5490 },
  50000: { credits: 50000, priceCents: 1899000, priceEur: 18990 },
};

// ---------------------------------------------------------------------------
// CreditPurchase — one-time credit pack
// ---------------------------------------------------------------------------

export type CreditPurchaseStatus =
  | 'pending' // checkout.session.completed but invoice not yet paid
  | 'awaiting_payment' // LEA-277: SEPA checkout completed, credits granted, waiting for bank transfer
  | 'requires_action' // LEA-505: refill invoice waits for the customer's 3DS confirmation
  | 'paid' // invoice.payment_succeeded fired
  | 'failed' // checkout abandoned / payment failed
  | 'refunded' // partial or full refund issued
  | 'partially_refunded';

// ---------------------------------------------------------------------------
// Ein-Klick-Nachkauf (LEA-505)
// ---------------------------------------------------------------------------
//
// Nach dem ersten Kontakt-Paket (LEA-516, Stripe Checkout) kann der Kunde
// weitere Pakete mit der dort gespeicherten Karte kaufen, ohne Umleitung. Es
// gibt KEINE neue Preisleiter: nachgekauft werden genau die CONTACT_PACKS zum
// regulären Preis. Ein Nachkauf ist nie ein Erstkauf (der Erstkauf läuft immer
// über Checkout, dort hängt der Erstkauf-Coupon), und es entsteht nirgends ein
// Abo — technisch eine einzelne Stripe-Rechnung mit automatischer Steuer.

/** Metadaten-`kind` der Nachkauf-Rechnung. Eigener Wert, damit kein Handler, der
 *  auf `contact_pack` hört, einen Nachkauf ein zweites Mal gutschreibt. */
export const CONTACT_PACK_REFILL_KIND = 'contact_pack_refill';

/** Welche Art Kauf eine creditPurchases-Zeile ist. Fehlt bei Alt-Zeilen. */
export type CreditPurchaseKind = 'credit_pack' | 'contact_pack' | typeof CONTACT_PACK_REFILL_KIND;

/**
 * Die Karte, die der Kunde beim Kontakt-Paket-Checkout ausdrücklich für
 * Nachkäufe gespeichert hat (users/{uid}.contactPackCard). Nur die Anzeige-
 * Felder — die Zahlungsdaten liegen ausschliesslich bei Stripe.
 */
export type ContactPackCard = {
  paymentMethodId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  savedAt: number;
  /** creditPurchases-Zeile des Checkouts, in dem die Einwilligung gegeben wurde. */
  sourcePurchaseId: string | null;
};

/** Warum der Ein-Klick-Nachkauf gerade nicht angeboten wird. Die UI zeigt dann
 *  nichts an — der normale Checkout bleibt der Weg. */
export type ContactPackRefillUnavailableReason =
  | 'disabled' // CONTACT_PACK_REFILL_ENABLED aus
  | 'billing_disabled'
  | 'not_reveal_model' // Bestandskunden sehen keine Änderung (LEA-518 Grandfathering)
  | 'first_purchase' // der Erstkauf läuft immer über Checkout (Erstkauf-Preis)
  | 'no_saved_card'
  | 'card_unusable' // abgelaufen, gelöscht oder einem anderen Kunden zugeordnet
  // Nicht die eigene Session des Kunden (Admin „Als Kunde ansehen", Backplane,
  // Dev-Bypass): abbuchen darf dort niemand, also wird auch nichts angeboten.
  | 'not_customer_session';

/** GET /api/billing/checkout/contact-pack/refill */
export type ContactPackRefillStatus =
  | {
      available: true;
      card: Pick<ContactPackCard, 'brand' | 'last4' | 'expMonth' | 'expYear'>;
      /** Netto-Listenpreise nur zur Auswahl — der verbindliche Betrag kommt aus /quote. */
      packs: Array<{ contacts: ContactPackSize; priceCents: number; configured: boolean }>;
    }
  | { available: false; reason: ContactPackRefillUnavailableReason };

/** GET /api/billing/contact-packs (LEA-516) — prices plus first-purchase status
 *  for the pack picker. LEA-518: read by the web app's first-purchase card. */
export type ContactPacksResponse = {
  /** The reveal balance (`creditsRemaining`), in contacts. */
  contactsRemaining: number;
  firstPurchase: boolean;
  discountAvailable: boolean;
  packs: Array<{
    contacts: ContactPackSize;
    /** Net list price. */
    priceCents: number;
    priceEur: number;
    popular: boolean;
    /** What the Checkout charges net: the first-purchase price if discounted. */
    effectivePriceCents: number;
    /** 0 without a discount. */
    discountCents: number;
    discountAvailable: boolean;
    /** False when the pack has no Stripe product on this server; never offer it. */
    configured: boolean;
  }>;
};

/** POST /api/billing/checkout/contact-pack/refill/quote — der Betrag, den die
 *  Bestätigungskarte zeigt, gerechnet von Stripe Tax für genau diesen Kunden. */
export type ContactPackRefillQuote = {
  packSize: ContactPackSize;
  currency: string;
  /** Wird abgebucht, brutto. Genau diese Zahl muss der Client beim Kauf zurückschicken. */
  amountDueCents: number;
  subtotalCents: number;
  taxCents: number;
  reverseCharge: boolean;
  card: Pick<ContactPackCard, 'brand' | 'last4'>;
};

/** Antwort von .../refill/charge und .../refill/:purchaseId/complete. */
export type ContactPackRefillResult =
  | { status: 'succeeded'; purchaseId: string; contactsAdded: number; creditsRemaining: number }
  | { status: 'requires_action'; purchaseId: string; clientSecret: string; publishableKey: string }
  | { status: 'processing'; purchaseId: string }
  | { status: 'failed'; purchaseId: string; code: string };

export type CreditPurchase = {
  id: string; // doc id (we generate)
  userId: string;
  userEmail: string; // denormalized for admin search
  /** LEA-505: fehlt bei Alt-Zeilen (dort: Credit- oder Kontakt-Paket per Checkout). */
  kind?: CreditPurchaseKind;

  // Stripe linking
  stripeCustomerId: string;
  /** null bei einem Ein-Klick-Nachkauf (LEA-505) — der läuft über eine Rechnung. */
  stripeCheckoutSessionId: string | null;
  stripePaymentIntentId: string | null; // populated after success
  stripeInvoiceId: string | null;
  stripeChargeId: string | null;

  // LEA-230 (Om 2026-05-03): hosted-invoice URLs (parallel to SubscriptionEvent
  // — see comment there). null on legacy/pending purchases; populated on
  // markPaid() when Stripe has issued an invoice for the Checkout session.
  invoicePdfUrl: string | null;
  hostedInvoiceUrl: string | null;

  // Product
  packSize: CreditPackSize;
  creditsGranted: number; // == packSize when paid; 0 while pending

  // Money
  amountCents: number;
  currency: 'eur';

  // Lifecycle
  status: CreditPurchaseStatus;
  createdAt: number;
  paidAt: number | null;
  refundedAt: number | null;
  refundedAmountCents: number; // accumulated across multiple partial refunds
  creditsClawedBack: number; // credits removed due to refund (for audit)

  // Linking back to credit-event ledger
  creditEventId: string | null; // the +N credit event written when paid

  // LEA-505 — nur bei Ein-Klick-Nachkäufen gesetzt.
  /** Vom Client pro Bestätigungskarte erzeugt; bildet die Doc-Id und alle
   *  Stripe-Idempotency-Keys. Ein wiederholter Klick trifft dieselbe Zeile. */
  confirmationId?: string;
  stripePaymentMethodId?: string | null;
  /** Was Stripe tatsächlich abgebucht hat, brutto inkl. Steuer. `amountCents`
   *  bleibt wie bei Checkout-Zeilen der Netto-Paketpreis. */
  amountPaidCents?: number | null;
  /** Warum ein Nachkauf nicht zustande kam (decline code, 'amount_changed', …). */
  failureCode?: string | null;
};

// ---------------------------------------------------------------------------
// Subscription — current state (one per user, keyed by uid)
// ---------------------------------------------------------------------------

export type SubscriptionStatus =
  | 'trialing' // local trial OR Stripe trial_period
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid'
  | 'paused';

// Promotion-Adaption (LEA-554-Release): auf staging kommt dieser Typ mit dem
// Prism-2-Mirror-Commit; hier nur der Typ + das optionale Feld, damit die
// LEA-554-Erkennung bezahlter Trials (`sub.kind === 'prism2_trial'`) zeichen-
// gleich mit staging bleibt. Der production-Webhook schreibt `kind` (noch)
// nicht — das Feld bleibt null, die Erkennung traegt der Live-Stripe-Read
// (`live.metadata.kind`) plus der Tripwire-Preisvergleich.
export type SubscriptionKind = 'prism2_trial';

export type SubscriptionDoc = {
  userId: string; // also the doc id
  userEmail: string;

  // Stripe linking — always present once anyone has signed up; null only if
  // user never went through the trial-grant + customer-create flow.
  stripeCustomerId: string;
  stripeSubscriptionId: string | null; // null during local trial without paid sub
  stripePriceId: string | null;
  stripeProductId: string | null;

  // Plan + credits
  tier: SubscriptionTier | null;
  creditsPerMonth: number;
  pricePerMonthCents: number;
  currency: 'eur';

  // Lifecycle
  status: SubscriptionStatus;
  isTrial: boolean;
  trialEndsAt: number | null;

  // Period (Stripe drives these via `current_period_start/end`)
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: number | null;

  // LEA-299: scheduled downgrade-at-period-end.
  //
  // Janik (2026-06-01, LEA-299): a downgrade takes effect at the END of the
  // current billing period (the user keeps their higher plan until renewal),
  // and existing credits are kept — only the monthly allowance lowers from the
  // next cycle. We implement that "switch later" with a Stripe
  // subscription_schedule (current price until period end, then the lower
  // price). These fields mirror that pending switch so the UI can render
  // "Downgrade to X on <date>" and offer a cancel. All null when nothing is
  // scheduled; cleared automatically once Stripe releases the schedule at
  // period end (see stripeWebhookHandler.handleSubscriptionUpdated).
  pendingTier?: SubscriptionTier | null; // the lower tier we switch to at period end
  pendingTierEffectiveAt?: number | null; // epoch ms — current_period_end when the switch applies
  stripeScheduleId?: string | null; // the Stripe subscription_schedule id (for cancel/release)

  // LEA-379 (Prism Stufe 2): tripwire. `delayedStartAt` = epoch-ms when the
  // scheduled Starter phase activates (day 4); until then the day-4 subscription
  // lives on `stripeScheduleId` above (cancel-before-day-4 releases it).
  // `tripwirePackPurchaseId` = the 49 € pack_purchase that granted the immediate
  // 50 credits. Both null for normal subs. IMPORTANT: like pendingTier /
  // firstInvoicePending, these must be carried forward in
  // handleSubscriptionUpdated (upsert overwrites the mirror) — else an unrelated
  // subscription.updated between checkout and day 4 wipes them mid-window.
  delayedStartAt?: number | null;
  tripwirePackPurchaseId?: string | null;
  // LEA-379 Stufe 4: MONOTONIC "this schedule-tripwire has already been activated"
  // marker — set once when the €49/3-day sub first flips to `active` (the +50 grant +
  // €290 schedule attach happen then). Unlike stripeScheduleId (which cancel/pause/
  // downgrade legitimately release to null while the sub stays active), this is NEVER
  // cleared, so the isFirstActiveTripwire guard can't re-fire (→ no duplicate +50 /
  // resurrected schedule). Like the other tripwire markers it MUST be carried forward
  // in the handleSubscriptionUpdated upsert or an unrelated update would wipe it.
  tripwireActivatedAt?: number | null;

  // LEA-259: timestamp the `subscription-cancelled` confirmation was actually
  // delivered (set only after a confirmed Postmark send). Drives single-send
  // idempotency across the `subscription.updated`→`subscription.deleted`
  // lifecycle — the deleted-handler treats a missing value as "still owed" and
  // sends, so a failed updated-time send is retried at deletion. Reset to null
  // on reactivation so a future re-cancellation can email again.
  cancellationEmailSentAt: number | null;

  // LEA-324: a SEPA (delayed-settlement) subscription whose FIRST invoice was
  // granted optimistically at checkout but whose debit has not yet settled.
  // true while the bank transfer is clearing; cleared on the first
  // invoice.payment_succeeded; the first-invoice failure path
  // (invoice.payment_failed, billing_reason='subscription_create') revokes the
  // grant. Drives the in-app "payment processing" banner via
  // BillingStatePublic.paymentProcessing. Optional so legacy docs (nullish)
  // read as "not pending". NOTE: SubscriptionRepository.upsert fully overwrites
  // the mirror, so this must be carried forward in handleSubscriptionUpdated
  // (like cancellationEmailSentAt / pendingTier) and is otherwise only mutated
  // via SubscriptionRepository.patch.
  firstInvoicePending?: boolean;

  // LEA-333: Stripe pause_collection state. When collection is paused, Stripe
  // stops generating renewal invoices (so monthly credit accrual naturally halts
  // — accrual is driven only by invoice.payment_succeeded). The user keeps access
  // + remaining credits; nothing is charged. IMPORTANT: pause_collection does NOT
  // change `status` — it stays 'active' (the dedicated 'paused' status is only for
  // trials that end without a payment method), so `pausedAt != null` is the pause
  // signal, NOT status. Unlike cancellationEmailSentAt/firstInvoicePending these
  // are DERIVED from the Stripe object (`pause_collection`) in
  // handleSubscriptionUpdated — like cancelAtPeriodEnd — so they auto-clear to
  // null on resume (pause_collection gone). pausedAt is stamped when we first see
  // the pause; pauseResumesAt mirrors Stripe's pause_collection.resumes_at. All
  // epoch ms.
  //
  // LEA-524 (Janik 2026-08-13): es gibt keine unbefristeten Pausen mehr. Jede neue
  // Pause traegt eine verbindliche Wiederaufnahme nach 1, 2 oder 3 Monaten, und
  // pauseCollectionBehavior ist 'void' statt 'keep_as_draft' — gehaltene Drafts
  // waren in beiden Zukuenften falsch (spaeter eingezogen = Rueckwirkend-Abrechnen
  // der Gratismonate, nie eingezogen = stiller Umsatzverlust). `pauseResumesAt ==
  // null` oder behavior 'keep_as_draft' kennzeichnet ab jetzt eine ALT-Pause von
  // vor diesem Deploy; der Watchdog meldet beides.
  pausedAt?: number | null;
  pauseResumesAt?: number | null;
  pauseCollectionBehavior?: 'keep_as_draft' | 'mark_uncollectible' | 'void' | null;
  // LEA-333: dedup marker for the "pause ending soon" reminder cron (set when
  // the reminder is sent; reset to null on each new pause so a re-pause can
  // remind again). Mirrors trialEndingNotifiedAt.
  pauseEndingNotifiedAt?: number | null;

  // LEA-554: Opt-in "erinnere mich 24 h vor Trial-Ende, statt jetzt zu
  // kuendigen" aus dem Trial-Intercept. Reiner Marker, keine Stripe-Aenderung;
  // idempotent (der erste Klick gewinnt, spaetere sind No-ops). Wie
  // reminderM2SentAt per set+merge geschrieben und im
  // handleSubscriptionUpdated-Upsert carry-forwarded — upsert() ist ein voller
  // set(), ohne die Uebernahme wuerde das naechste beliebige
  // subscription.updated das Opt-in loeschen und wir brechen genau das
  // Versprechen, das den Kuendiger gehalten hat.
  trialReminderRequestedAt?: number | null;

  // LEA-554: MONOTONER Marker "diese Sub wurde WAEHREND des Stripe-Trials
  // sofort beendet" (nicht cancel_at_period_end). Zwei Leser haengen daran:
  // (1) die Cancellation-Mail — ohne diesen Marker schickt der deleted-Handler
  //     `accessUntil = currentPeriodEnd`, und das ist bei einer trialing-Sub
  //     exakt trial_end, also ein ZUKUENFTIGES Datum. Die Mail wuerde Zugang
  //     versprechen, den es nicht mehr gibt.
  // (2) LEA-560 Win-back — "Trial-Sofort-Kuendiger" ist sonst nicht von einem
  //     normalen Kuendiger unterscheidbar: handleSubscriptionDeleted schreibt
  //     isTrial nie um, und canceledAt allein sagt nichts ueber den Zeitpunkt
  //     im Trial-Lebenszyklus.
  // Wird VOR dem Stripe-Call gestempelt (damit der Webhook ihn garantiert
  // sieht) und im Fehlerfall wieder geraeumt. Nie regulaer genullt.
  trialCanceledAt?: number | null;

  // Promotion-Adaption (LEA-554-Release): siehe SubscriptionKind oben — auf
  // production schreibt der Webhook dieses Feld noch nicht, es bleibt null.
  kind?: SubscriptionKind | null;

  // LEA-557: EINMAL pro Kunde (Janik 04.09.) — Zeitstempel der Einloesung des
  // Retention-Coupons. Zwei Leser: die Route (zweiter Klick -> alreadyApplied)
  // und der /state-Builder (Hebel ausblenden). Per set+merge geschrieben und im
  // handleSubscriptionUpdated-Upsert carry-forwarded — SELBSTVERSCHULDETE Falle
  // sonst: das Anlegen des Coupons feuert genau das subscription.updated, das
  // den Marker ohne Carry-forward-Zeile wieder loeschen wuerde, und der Kunde
  // koennte erneut einloesen.
  retentionDiscountUsedAt?: number | null;

  // LEA-558: MONOTONER Marker "VIP-Einrichtung angefragt" — dedupliziert die
  // Gruender-Benachrichtigung (zweiter Klick pingt Janik nicht erneut) und
  // wird im handleSubscriptionUpdated-Upsert carry-forwarded. Anders als
  // trialReminderRequestedAt OHNE no_mirror-Verweigerung geschrieben: die
  // Zielgruppe des Angebots (Trial, 0 Leads) kann genau der Kohorte ohne
  // Mirror-Doc angehoeren, und hier liest keine Range-Query das Shell-Doc —
  // es ist harmlos und der Klick darf nicht verloren gehen.
  vipSetupRequestedAt?: number | null;

  // LEA-560: Dedupe-Marker der Win-back-Mail — genau EINE pro
  // Trial-Sofort-Kuendigung, gestempelt erst NACH bestaetigtem Send
  // (LEA-556-Vertrag). Carry-forward im Upsert wie alle unsere Marker;
  // praktisch feuern auf einer geloeschten Sub kaum noch Events, aber ein
  // Grenzfall-Event darf den Marker nicht wegwischen und die Mail doppeln.
  winbackSentAt?: number | null;

  // Bookkeeping
  createdAt: number;
  updatedAt: number;
};

// ---------------------------------------------------------------------------
// SubscriptionEvent — append-only audit of state changes
// ---------------------------------------------------------------------------

export type SubscriptionEventType =
  | 'trial_started'
  | 'trial_ended'
  | 'subscription_created'
  | 'subscription_updated'
  | 'subscription_renewed'
  | 'payment_succeeded'
  | 'payment_failed'
  | 'subscription_canceled'
  | 'subscription_resumed';

export type SubscriptionEvent = {
  id: string;
  userId: string;
  type: SubscriptionEventType;
  timestamp: number;

  // Snapshot at event time (denormalized for fast admin queries — never updated)
  tier: SubscriptionTier | null;
  status: SubscriptionStatus;

  // Money (only payment_* events)
  amountCents?: number | null;
  currency?: 'eur';
  stripeInvoiceId?: string | null;
  stripeChargeId?: string | null;

  // LEA-230 (Om 2026-05-03): hosted-invoice URLs Stripe attaches to the
  // invoice. Persisted on the event at webhook time so the Account UI's
  // "Download PDF" button can deep-link directly to the PDF instead of
  // bouncing the user through the Stripe Customer Portal. Both URLs are
  // permanently signed by Stripe (no expiry); persist as-received.
  //   invoicePdfUrl    → invoice.invoice_pdf       (direct .pdf download)
  //   hostedInvoiceUrl → invoice.hosted_invoice_url (Stripe-rendered HTML)
  invoicePdfUrl?: string | null;
  hostedInvoiceUrl?: string | null;

  // Free-form context
  reason?: string;
  metadata?: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Refund — every refund issued (admin-initiated or Stripe-driven)
// ---------------------------------------------------------------------------

export type RefundOrigin = 'admin' | 'stripe_dashboard' | 'webhook' | 'customer_portal';

export type Refund = {
  id: string;
  userId: string;
  userEmail: string;

  // Stripe linking
  stripeChargeId: string;
  stripePaymentIntentId: string | null;
  stripeRefundId: string;

  // Money
  amountCents: number;
  currency: 'eur';

  // Linking back to source
  // Either a credit pack OR a subscription invoice — exactly one populated.
  creditPurchaseId: string | null;
  subscriptionInvoiceId: string | null;

  // Credit clawback
  creditsClawedBack: number; // ACTUAL credits removed from the ledger (post-clamp).
  // 0 if no clawback (e.g. customer-courtesy refund). See intendedClawback below.
  clawbackPolicy: 'full' | 'prorated' | 'none';
  // LEA-377 B4: the pre-clamp pro-rata amount we *intended* to remove, and the
  // shortfall (intended − actual) that the user consumed before the refund and
  // therefore keeps. `clawbackShortfall > 0` is the auditable "leads kept but no
  // longer paid for" figure (AC#5). Optional for backwards-compat with old docs.
  intendedClawback?: number;
  clawbackShortfall?: number;
  // LEA-377 B8: set when a refund later transitioned to `failed` and we re-granted
  // the clawed-back credits (money was never actually returned). Idempotency guard.
  clawbackReversed?: boolean;

  // Lifecycle
  origin: RefundOrigin;
  reason: string | null;
  initiatedBy: string | null; // admin uid if origin === 'admin'
  createdAt: number;
};

// ---------------------------------------------------------------------------
// StripeEventProcessed — idempotency guard (event.id is the doc id)
// ---------------------------------------------------------------------------

export type StripeEventProcessed = {
  id: string; // == Stripe event.id (evt_xxx)
  type: string; // event.type for debugging
  receivedAt: number;
  processedAt: number; // when we finished handling
  status: 'pending' | 'ok' | 'error';
  error: string | null;
};

// ---------------------------------------------------------------------------
// Public projection — what the frontend reads via GET /api/billing/state
// ---------------------------------------------------------------------------

export type BillingStatePublic = {
  // Plan
  tier: SubscriptionTier | null;
  status: SubscriptionStatus;
  isTrial: boolean;
  // LEA-554 (Janik, 04.09.): true fuer Standard-Trials (Cancel endet sofort),
  // false fuer BEZAHLTE Trials (Tripwire 49 EUR, prism2) — dort laeuft der
  // Cancel zum Periodenende, und die Kuendigungsstrecke muss ihre
  // "endet sofort"-Zusage entsprechend umschalten.
  trialCancelsImmediately: boolean;
  // LEA-557: ob der 30%-Retention-Hebel fuer DIESEN Kunden anzeigbar ist —
  // Coupon konfiguriert, Standard-Tier-Preis (mono99/Founder/Intro/Tripwire
  // per Preis-Whitelist raus), kein aktiver Sperr-Zustand, noch nie eingeloest.
  // Client-Polarity: bei fehlendem Feld gilt FALSE — ein alter Payload darf nie
  // einen Rabatt bewerben, den der Server dann verweigert.
  retentionDiscountAvailable: boolean;
  trialEndsAt: number | null;

  // Credits
  creditsRemaining: number;
  monthlyCreditsAllowance: number;
  // Credits actually consumed (lead deliveries) in the current cycle. This is
  // real usage — refund clawbacks / grants are NOT counted. The UI should
  // render "used this month" from this, not from (allowance − creditsRemaining),
  // which mis-counts a refund as usage (LEA-230 / refund display bug).
  monthlyCreditsUsed: number;
  cycleResetAt: number;

  // Period
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;

  // LEA-299: a downgrade scheduled to take effect at period end. `pendingTier`
  // is the lower plan the user will move to; `pendingTierEffectiveAt` is when
  // (epoch ms). Both null when no downgrade is scheduled.
  pendingTier: SubscriptionTier | null;
  pendingTierEffectiveAt: number | null;

  // LEA-379 (Prism Stufe 2): a tripwire Starter subscription scheduled to auto-
  // activate on day 4 (epoch ms), or null/undefined when not on a tripwire. The
  // UI renders "Starter starts on <date>" + the pre-day-4 cancel affordance.
  // Optional so existing state resolvers stay valid until the billing arm wires it.
  delayedSubscriptionStartsAt?: number | null;

  // Stripe
  hasStripeCustomer: boolean;
  hasActiveSubscription: boolean;

  // LEA-546: the two routing predicates the plan-switch UI needs, answered by
  // the server so the client cannot disagree with it.
  //
  // hasActiveSubscription is NOT the right question for either one. It is not
  // the guard set of POST /subscription/switch|downgrade (those also 409 on
  // cancelAtPeriodEnd and on a null tier), and it reads FALSE for a LEA-379
  // tripwire that exists only as a Stripe schedule — where a fresh Checkout
  // would mint a SECOND subscription. It stays as-is because the LEA-241
  // post-checkout poll depends on its exact meaning.
  //
  // canChangePlan: an in-place switch or a scheduled downgrade would be
  // accepted right now.
  canChangePlan: boolean;
  // The RAW mirror tier — deliberately not the display `tier` above, which is
  // forced to 'starter' during the tripwire intro. Only meaningful while
  // canChangePlan is true; it is what the downgrade-direction check compares.
  planChangeTier: SubscriptionTier | null;
  // "A fresh Checkout here would duplicate something." True for any non-terminal
  // subscription AND for a pre-activation tripwire schedule. False for canceled
  // / incomplete_expired — those are dead and MUST stay re-buyable (LEA-475).
  hasSubscriptionCommitment: boolean;

  // LEA-324: the subscription's first invoice was granted optimistically at
  // SEPA checkout and its debit is still clearing (true == "payment
  // processing"). The in-app banner gates on this. Distinct from a failed/
  // past_due payment — it means "all good, settling". Cleared once the first
  // invoice settles (or the grant is revoked on a returned debit).
  paymentProcessing: boolean;

  // LEA-333: subscription is paused (Stripe pause_collection). Access + remaining
  // credits are retained, no new monthly allowance, nothing charged. The UI shows
  // a "paused" state + a resume action. pauseResumesAt is the auto-resume date
  // (epoch ms) or null for manual/indefinite resume (the v1 default).
  isPaused: boolean;
  pauseResumesAt: number | null;

  // Convenience
  canPurchaseCredits: boolean;
  canManageSubscription: boolean;
};
